import { Component, Inject, OnInit } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { AlgoSpace, AlgoSpaceMapping, ConnectedBroker, SpaceMappingResult } from '../manage-broker.interface';
import { ManageBrokerService } from '../manage-broker.service';
import { NgxToastrService } from '@app/_shared';
import { forkJoin } from 'rxjs';
import { MixpanelService } from '@app/_shared/providers/mixpanel/mixpanel.service';
import { MIXPANEL_EVENTS } from '@app/_shared/providers/mixpanel/mixpanel.events';

/**
 * Assign / unassign algo spaces for a broker connection.
 * FE pre-validates mappable status and removal when logged-in + running algos (UT-9061).
 * Use refresh to reload space list and running-algo state; BE remains authoritative on submit.
 */
@Component({
  selector: 'app-map-algo-space',
  templateUrl: './map-algo-space.component.html',
  styleUrls: ['./map-algo-space.component.scss']
})
export class MapAlgoSpaceComponent implements OnInit {
  algoSpaces: AlgoSpace[] = [];
  selectedSpaceIds = new Set<string>();
  isLoading = true;
  isRefreshing = false;
  isSubmitting = false;
  brokerHasRunningAlgos = false;
  brokerDisplayName = '';
  brokerImageLink = '/assets/utradeSymbol.svg';

  constructor(
    @Inject(MAT_LEGACY_DIALOG_DATA) public data: { brokerDetails: ConnectedBroker, from: string },
    private dialogRef: MatDialogRef<MapAlgoSpaceComponent>,
    private snackBar: NgxToastrService,
    private manageBrokerService: ManageBrokerService,
    private mixpanel: MixpanelService,
  ) {}

  ngOnInit(): void {
    const broker = this.data.brokerDetails;
    const brokerInfo = this.manageBrokerService.cachedAvailableBrokers?.[broker.broker];
    this.brokerDisplayName = brokerInfo?.display_name ?? broker.broker;
    this.brokerImageLink = brokerInfo?.image_link ?? '/assets/utradeSymbol.svg';
    broker.algo_spaces?.forEach((space) => this.selectedSpaceIds.add(space.id));
    this.loadAlgoSpaceContext();
    this.fireMixpanelEvent(MIXPANEL_EVENTS.VIRTUAL_ALGO_SPACE_DASHBOARD, {
      from: this.data.from,
    });
  }

  refreshAlgoSpaces(): void {
    if (this.isLoading || this.isRefreshing || this.isSubmitting) {
      return;
    }
    this.loadAlgoSpaceContext(true);
  }

  getMapBlockReason(space: AlgoSpace, wantsMapped: boolean): string | null {
    return this.manageBrokerService.getAlgoSpaceMapBlockReason(
      space,
      this.data.brokerDetails,
      wantsMapped,
      this.brokerHasRunningAlgos,
    );
  }

  getSpaceDisabledTooltip(space: AlgoSpace): string {
    const isSelected = this.selectedSpaceIds.has(space.id);
    return this.getMapBlockReason(space, !isSelected) ?? '';
  }

  isSpaceInteractionDisabled(space: AlgoSpace): boolean {
    const isSelected = this.selectedSpaceIds.has(space.id);
    return !!this.getMapBlockReason(space, !isSelected);
  }

  toggleSpace(space: AlgoSpace, checked?: boolean): void {
    const isSelected = this.selectedSpaceIds.has(space.id);
    const wantsMapped = checked ?? !isSelected;
    if (this.getMapBlockReason(space, wantsMapped)) {
      return;
    }
    wantsMapped
      ? (this.selectedSpaceIds = new Set([...this.selectedSpaceIds, space.id]))
      : (this.selectedSpaceIds = new Set([...this.selectedSpaceIds].filter((id) => id !== space.id)));
  }

  onSpaceOptionClick(space: AlgoSpace): void {
    if (this.isSpaceInteractionDisabled(space)) {
      return;
    }
    this.toggleSpace(space);
  }

  getOtherMappedBrokerCount(space: AlgoSpace): number {
    return Math.max(0, (space.mapped_broker_count ?? 0) - 1);
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  redirectToBuyAlgoSpace(): void {
    this.manageBrokerService.redirectToBuyAlgoSpace();
  }

  mapSpace(): void {
    const spaces = this.getChangedSpaceMappings();
    if (!spaces.length || this.isSubmitting) {
      return;
    }
    for (const mapping of spaces) {
      const space = this.algoSpaces.find((item) => item.id === mapping.space_id);
      if (!space) {
        continue;
      }
      const blockReason = this.getMapBlockReason(space, mapping.mapped);
      if (blockReason) {
        this.snackBar.simpleCustomToaster(blockReason, false);
        return;
      }
    }
    this.isSubmitting = true;
    const { id: connectionId } = this.data.brokerDetails;
    this.manageBrokerService.mapAlgoSpace(connectionId, spaces).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        const results = response.data?.results ?? [];
        const failures = results.filter((result) => !result.success);
        const successes = results.filter((result) => result.success);

        failures.forEach((result) => {
          this.snackBar.simpleCustomToaster(result.error || result.message || 'Unable to update algo space mapping.', false);
        });

        if (successes.length) {
          const updatedBroker = this.manageBrokerService.applyMapAlgoSpaceResults(
            this.data.brokerDetails,
            results,
            this.algoSpaces,
          );
          this.snackBar.simpleCustomToaster(response.message || 'Algo space mapping updated.');
          this.fireMixpanelEvent(MIXPANEL_EVENTS.VIRTUAL_ALGO_SPACE_ASSIGNMENT_SUCCESS, {
            from: this.data.from,
            broker: updatedBroker.broker,
            ips_mapped: updatedBroker.algo_spaces?.map((space) => space.reserved_ip) ?? [],
          });
          this.dialogRef.close(updatedBroker);
          return;
        }

        this.dialogRef.close();
      },
      error: (error) => {
        this.isSubmitting = false;
        this.snackBar.simpleCustomToaster(error?.error?.message ?? 'Unable to update algo space mapping.', false);
      },
    });
  }

  fireMixpanelEvent(event: string, params: Record<string, any>) {
    try {
      this.mixpanel.track(event, params);
    } catch (error) { }
  }

  hasSelectionChanged(): boolean {
    return this.getChangedSpaceMappings().length > 0;
  }

  private loadAlgoSpaceContext(isRefresh = false): void {
    if (isRefresh) {
      this.isRefreshing = true;
    } else {
      this.isLoading = true;
    }

    forkJoin({
      algoSpaces: this.manageBrokerService.getAlgoSpaces(),
      hasRunningAlgos: this.manageBrokerService.hasRunningAlgosOnBroker(this.data.brokerDetails.broker),
    }).subscribe({
      next: ({ algoSpaces, hasRunningAlgos }) => {
        this.algoSpaces = algoSpaces.data ?? [];
        this.brokerHasRunningAlgos = hasRunningAlgos;
        const freshBroker = this.manageBrokerService.getConnectedBrokerByConnectionId(this.data.brokerDetails.id);
        if (freshBroker) {
          this.data.brokerDetails = {
            ...this.data.brokerDetails,
            status: freshBroker.status,
            algo_spaces: freshBroker.algo_spaces,
          };
          if (!isRefresh) {
            this.selectedSpaceIds = new Set(freshBroker.algo_spaces?.map((space) => space.id) ?? []);
          }
        }
        this.isLoading = false;
        this.isRefreshing = false;
      },
      error: () => {
        this.isLoading = false;
        this.isRefreshing = false;
        this.snackBar.simpleCustomToaster('Failed to load algo spaces. Please refresh.', false);
      },
    });
  }

  private getChangedSpaceMappings(): AlgoSpaceMapping[] {
    const initial = new Set(this.data.brokerDetails.algo_spaces?.map((space) => space.id) ?? []);
    const allIds = new Set([...initial, ...this.selectedSpaceIds]);
    return [...allIds]
      .filter((spaceId) => initial.has(spaceId) !== this.selectedSpaceIds.has(spaceId))
      .map((spaceId) => ({
        space_id: spaceId,
        mapped: this.selectedSpaceIds.has(spaceId),
      }));
  }

  trackBySpaceId(_: number, space: AlgoSpace): string {
    return space.id;
  }
}
